import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Clock, Rocket, X, Gift, Undo2 } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TONE = {
  info: { ring: 'border-emerald-500/30 bg-emerald-500/10', icon: Sparkles, tint: 'text-emerald-400', btn: 'bg-emerald-500 hover:bg-emerald-400 text-[#0a0e1a]' },
  feature: { ring: 'border-blue-500/30 bg-blue-500/10', icon: Gift, tint: 'text-blue-400', btn: 'bg-blue-500 hover:bg-blue-400 text-white' },
  urgent: { ring: 'border-amber-500/40 bg-amber-500/10', icon: Clock, tint: 'text-amber-400', btn: 'bg-amber-500 hover:bg-amber-400 text-[#0a0e1a]' },
  final: { ring: 'border-red-500/40 bg-red-500/10', icon: Clock, tint: 'text-red-400', btn: 'bg-red-500 hover:bg-red-400 text-white' },
  recovery: { ring: 'border-sky-500/30 bg-sky-500/10', icon: Undo2, tint: 'text-sky-400', btn: 'bg-sky-500 hover:bg-sky-400 text-white' },
};

// Subtle, dismissible, behaviour/day-aware trial conversion nudge.
export default function TrialNudge() {
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_URL}/trial/nudge`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const d = r.data;
        if (!d?.show) return;
        // Show each stage at most once per day (non-nagging).
        const dismissKey = `sk_nudge_${d.key}`;
        if (sessionStorage.getItem(dismissKey) === 'closed') return;
        setNudge({ ...d, dismissKey });
      })
      .catch(() => {});
  }, []);

  if (!nudge) return null;
  const t = TONE[nudge.tone] || TONE.info;
  const Icon = t.icon;
  const close = () => {
    try { sessionStorage.setItem(nudge.dismissKey, 'closed'); } catch (e) { /* ignore */ }
    setNudge(null);
  };

  return (
    <div className={`mb-4 rounded-2xl border ${t.ring} px-4 py-3 flex items-center gap-3`} data-testid="trial-nudge">
      <div className={`shrink-0 ${t.tint}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white truncate" data-testid="trial-nudge-headline">{nudge.headline}</div>
        <div className="text-xs text-zinc-400 line-clamp-2">{nudge.body}</div>
      </div>
      <button
        onClick={() => navigate(nudge.cta_href || '/app/subscription')}
        className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold ${t.btn} transition-colors`}
        data-testid="trial-nudge-cta">
        <Rocket className="w-3.5 h-3.5" /> {nudge.cta_label}
      </button>
      <button onClick={close} className="shrink-0 text-zinc-500 hover:text-white" data-testid="trial-nudge-close" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
